/**
 * F-companies-default — Hook unificado para gerenciar a empresa selecionada
 * no company switcher de TODAS as páginas.
 *
 * Cascata de seleção default:
 *   1. ID persistido em localStorage (escolha explícita do usuário)
 *      → só usa se ainda pertence a `accessibleCompanies`
 *   2. `isPrimary === true` (empresa marcada como principal no schema)
 *   3. Primeiro da lista por `displayOrder` (já vem ordenado do backend)
 *
 * Persistência: a cada `setCompanyId(...)`, escreve em
 * `localStorage["eurostorm:selectedCompanyId"]`. Próxima sessão recupera.
 *
 * Limpa o localStorage automaticamente quando a empresa persistida some da
 * lista (foi desativada / removida do allowedCompanyIds).
 *
 * IMPORTANTE: usado por 17 páginas — substitui o pattern errado
 * `useState<number | null>(accessibleCompanies[0]?.id ?? null)` que pegava
 * a primeira empresa alfabeticamente (B vinha antes de E).
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

const STORAGE_KEY = "eurostorm:selectedCompanyId";

interface CompanyLike {
  id: number;
  isPrimary?: boolean;
  displayOrder?: number;
}

function readPersisted(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writePersisted(id: number | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id == null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    // Falha silenciosa (localStorage pode estar bloqueado em modo privado).
  }
}

/* ─── Store compartilhado ────────────────────────────────────────────────
 *
 * F-company-sync — ANTES cada componente guardava a empresa no seu próprio
 * useState. Trocar a empresa na sidebar não avisava a página (e vice-versa):
 * o localStorage mudava, mas o outro componente seguia com o valor antigo
 * até remontar. Na prática dava para ver "Euro Storm" na sidebar enquanto a
 * listagem consultava a Brazilian Storms — e concluir que o dossier sumiu.
 *
 * Agora o valor vive fora do React e todos os componentes se inscrevem via
 * useSyncExternalStore, então a troca é instantânea em todas as telas.
 * O listener de `storage` mantém abas diferentes coerentes também.
 */
let currentCompanyId: number | null = readPersisted();
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): number | null {
  return currentCompanyId;
}

/** Snapshot no SSR/primeiro paint — sem window, não há persistido. */
function getServerSnapshot(): number | null {
  return null;
}

/** Fonte única de escrita: persiste, atualiza o valor e notifica todos. */
function setShared(id: number | null): void {
  if (currentCompanyId === id) return;
  currentCompanyId = id;
  writePersisted(id);
  emit();
}

if (typeof window !== "undefined") {
  // Outra aba trocou de empresa → acompanha.
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    const n = e.newValue == null ? null : Number(e.newValue);
    currentCompanyId = Number.isFinite(n) && n !== null && n > 0 ? n : null;
    emit();
  });
}

export function resolveDefaultCompanyId(
  accessibleCompanies: ReadonlyArray<CompanyLike>,
  persisted: number | null,
): number | null {
  if (accessibleCompanies.length === 0) return null;
  // 1. Persisted se ainda acessível
  if (persisted != null && accessibleCompanies.some((c) => c.id === persisted)) {
    return persisted;
  }
  // 2. Primary
  const primary = accessibleCompanies.find((c) => c.isPrimary === true);
  if (primary) return primary.id;
  // 3. First (lista já vem ordenada por displayOrder do backend)
  return accessibleCompanies[0]!.id;
}

/**
 * Hook reutilizado pelas listagens / wizards / detalhes que precisam selecionar
 * uma empresa do grupo. Retorna `[companyId, setCompanyId]`.
 *
 * O hook lê `accessibleCompanies` (filtrado pelos memberships do user),
 * resolve o default via cascata acima, e persiste a escolha do user em
 * localStorage. Quando a lista de accessibleCompanies muda (re-fetch),
 * re-aplica a cascata.
 */
export function useSelectedCompany(
  accessibleCompanies: ReadonlyArray<CompanyLike>,
): [number | null, (id: number | null) => void] {
  const companyId = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Re-aplica a cascata quando a lista de empresas muda (login, re-fetch,
  // perdeu acesso a uma empresa). Roda só quando os IDs efetivamente mudam,
  // não a cada render — memorizar IDs ordenados estabiliza a dependência.
  const idsSignature = useMemo(
    () => accessibleCompanies.map((c) => c.id).join(","),
    [accessibleCompanies],
  );

  useEffect(() => {
    // Aguarda companies query carregar. Sem a lista, não podemos validar se
    // o ID persistido ainda é válido — pular o efeito evita LIMPAR o
    // localStorage indevidamente durante o estado de "loading".
    if (accessibleCompanies.length === 0) return;
    const persisted = currentCompanyId ?? readPersisted();
    const resolved = resolveDefaultCompanyId(accessibleCompanies, persisted);
    // Cobre também o caso da empresa persistida ter perdido acesso: resolved
    // vem diferente e setShared já regrava o localStorage.
    if (resolved !== currentCompanyId) setShared(resolved);
    // accessibleCompanies muda referência a cada render; idsSignature estabiliza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsSignature]);

  const setCompanyId = useCallback((id: number | null) => {
    setShared(id);
  }, []);

  return [companyId, setCompanyId];
}
